import { useCallback, useEffect, useMemo, useState } from "react";
import { BookingStatus, SHOOT_TYPE_LABELS, ShootType } from "@photography-booking/shared";
import {
  decideAdminBooking,
  deleteAdminBooking,
  listAdminBookings,
  messageBookingClient,
  updateAdminBooking,
  type AdminBooking,
  type AdminBookingUpdate,
  type BookingDecisionAction,
} from "../api/admin-client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";

/**
 * The editable draft for the inline booking editor. Only the schedule is
 * editable for any booking; the status is editable only when the booking is
 * currently DENIED (to reverse a rejection).
 */
interface BookingDraft {
  requestedDate: string;
  startTime: string;
  durationMinutes: string;
  status: BookingStatus;
}

/** Seeds an edit draft from a booking (missing time fields become blank). */
function draftFromBooking(booking: AdminBooking): BookingDraft {
  return {
    requestedDate: booking.requestedDate ?? "",
    startTime: booking.startTime ?? "",
    durationMinutes:
      typeof booking.durationMinutes === "number"
        ? String(booking.durationMinutes)
        : "",
    status: booking.status as BookingStatus,
  };
}

interface BookingsAdminProps {
  token: string;
}

/** Sort priority for booking statuses: PENDING first, then the rest. */
const STATUS_ORDER: Record<string, number> = {
  [BookingStatus.PENDING]: 0,
  [BookingStatus.APPROVED]: 1,
  [BookingStatus.DENIED]: 2,
  [BookingStatus.EXPIRED]: 3,
};

/**
 * Orders bookings with PENDING first (the inquiries still awaiting a decision),
 * then by requested date ascending. Stable, pure, and testable in isolation.
 */
function sortBookings(bookings: readonly AdminBooking[]): AdminBooking[] {
  return [...bookings].sort((a, b) => {
    const byStatus =
      (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
    if (byStatus !== 0) {
      return byStatus;
    }
    return a.requestedDate.localeCompare(b.requestedDate);
  });
}

/** Renders a minute count as a friendly "H hour(s) M minute(s)" label. */
function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  }
  if (mins > 0) {
    parts.push(`${mins} minute${mins === 1 ? "" : "s"}`);
  }
  return parts.length > 0 ? parts.join(" ") : `${minutes} minutes`;
}

/**
 * Builds the "date · time · duration" summary line for a booking, gracefully
 * omitting the time and duration for legacy inquiries created before the
 * time-based model (which have no `startTime`/`durationMinutes`).
 */
function formatSchedule(booking: AdminBooking): string {
  const parts: string[] = [booking.requestedDate];
  if (booking.startTime) {
    parts.push(booking.startTime);
  }
  if (typeof booking.durationMinutes === "number" && booking.durationMinutes > 0) {
    parts.push(formatDuration(booking.durationMinutes));
  }
  if (parts.length === 1) {
    parts.push("no time specified");
  }
  if (booking.shootType) {
    parts.push(SHOOT_TYPE_LABELS[booking.shootType]);
  }
  return parts.join(" · ");
}

/** Lower-cased CSS status modifier, e.g. "pending" → `admin-badge--pending`. */
function statusModifier(status: string): string {
  return status.toLowerCase();
}

/**
 * The default Google Calendar event title suggested when approving a booking:
 * `"<requester name> — <shoot type>"`. The owner can edit it before confirming.
 */
function defaultCalendarTitle(booking: AdminBooking): string {
  const label = SHOOT_TYPE_LABELS[booking.shootType ?? ShootType.OTHER];
  // Plain hyphen (not an em-dash) so it matches the separators the owner types.
  return `${booking.clientName} - ${label}`;
}

/**
 * Builds a Google Calendar deep link to the day view for a `YYYY-MM-DD` date,
 * so the owner can open that exact day and see their full schedule. Returns
 * null for a malformed/absent date.
 */
function googleCalendarDayUrl(dateStr: string | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr ?? "");
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  return `https://calendar.google.com/calendar/u/0/r/day/${Number(year)}/${Number(
    month,
  )}/${Number(day)}`;
}

/*
 * NOTE: The "View this day" link is a plain web calendar URL opened in a new
 * tab on every platform. Deep-linking into the Google Calendar Android app to a
 * day (or week) view is not achievable from a web page: the only intent that
 * navigates the app to a date is `content://com.android.calendar/time/<ms>`,
 * which browsers refuse to launch, and the app does not register the web app's
 * `/r/day/...` navigation paths as App Links (only add-event and event links).
 * This was verified on-device — do not reintroduce an intent:// variant.
 */

/**
 * Read-only Bookings view (owner admin).
 *
 * Lists every booking inquiry from `GET /admin/bookings` with PENDING inquiries
 * first, then by requested date. For each booking it shows the client's name
 * and email, the requested date + start time + session length, a status badge,
 * the message, and an owner-only conflict indicator derived from the booking's
 * `conflicts` summary (overlapping approved bookings that day and/or a busy
 * calendar), or a subtle "No conflicts" when the day is clear.
 *
 * PENDING inquiries can be accepted or denied directly here (which sends the
 * client the same outcome email as the signed links in the notification
 * email), or the Owner can still act from those email links.
 */
export function BookingsAdmin({ token }: BookingsAdminProps): JSX.Element {
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BookingDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [editNotify, setEditNotify] = useState(false);
  const [pending, setPending] = useState<{
    kind: "accept" | "deny" | "delete";
    booking: AdminBooking;
    notify: boolean;
  } | null>(null);
  const [calendarTitle, setCalendarTitle] = useState("");
  // "Message client" composer: the booking being messaged + the draft text.
  const [messaging, setMessaging] = useState<AdminBooking | null>(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | BookingStatus>("ALL");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const { status, reportError, reportSuccess } = useAdminStatus();

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await listAdminBookings(token);
    setLoading(false);
    if (result.ok) {
      setBookings(result.data);
    } else {
      reportError(result.message);
    }
  }, [token, reportError]);

  /**
   * Sends the composed message to the booking's client. The email goes out from
   * the studio address (never the owner's personal one), so replies come back
   * through the studio inbox.
   */
  const sendMessage = async (): Promise<void> => {
    if (!messaging || messageDraft.trim().length === 0) {
      return;
    }
    setSendingMessage(true);
    const result = await messageBookingClient(
      token,
      messaging.bookingId,
      messageDraft.trim(),
    );
    setSendingMessage(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setMessaging(null);
    setMessageDraft("");
    reportSuccess("Message sent.");
  };

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const decide = useCallback(
    async (
      bookingId: string,
      action: BookingDecisionAction,
      notify: boolean,
      eventTitle?: string,
    ) => {
      setDeciding(bookingId);
      const result = await decideAdminBooking(
        token,
        bookingId,
        action,
        notify,
        action === "ACCEPT" ? eventTitle : undefined,
      );
      setDeciding(null);
      if (result.ok) {
        const verb = action === "ACCEPT" ? "approved" : "denied";
        reportSuccess(
          notify
            ? `Booking ${verb} — the requester has been emailed.`
            : `Booking ${verb} — the requester was not notified.`,
        );
        await refresh();
      } else {
        reportError(result.message);
      }
    },
    [token, refresh, reportError, reportSuccess],
  );

  const remove = useCallback(
    async (booking: AdminBooking, notify: boolean) => {
      setRemovingId(booking.bookingId);
      const result = await deleteAdminBooking(token, booking.bookingId, notify);
      setRemovingId(null);
      if (result.ok) {
        reportSuccess(
          notify
            ? "Booking deleted — the requester has been emailed."
            : "Booking deleted — the requester was not notified.",
        );
        await refresh();
      } else {
        reportError(result.message);
      }
    },
    [token, refresh, reportError, reportSuccess],
  );

  const beginEdit = useCallback((booking: AdminBooking) => {
    setEditingId(booking.bookingId);
    setDraft(draftFromBooking(booking));
    setEditNotify(false);
  }, []);

  const confirmPending = useCallback(async () => {
    if (!pending) {
      return;
    }
    const { kind, booking, notify } = pending;
    const titleForEvent = calendarTitle;
    setPending(null);
    if (kind === "delete") {
      await remove(booking, notify);
    } else {
      await decide(
        booking.bookingId,
        kind === "accept" ? "ACCEPT" : "DENY",
        notify,
        titleForEvent,
      );
    }
  }, [pending, remove, decide, calendarTitle]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setDraft(null);
  }, []);

  const saveEdit = useCallback(
    async (bookingId: string, notify: boolean) => {
      if (!draft) {
        return;
      }
      const duration = Number.parseInt(draft.durationMinutes, 10);
      const updates: AdminBookingUpdate = {
        requestedDate: draft.requestedDate.trim(),
        startTime: draft.startTime.trim(),
        status: draft.status,
      };
      if (Number.isFinite(duration)) {
        updates.durationMinutes = duration;
      }
      setSaving(true);
      const result = await updateAdminBooking(token, bookingId, updates, notify);
      setSaving(false);
      if (result.ok) {
        reportSuccess(
          notify ? "Booking updated — the requester has been emailed." : "Booking updated.",
        );
        setEditingId(null);
        setDraft(null);
        await refresh();
      } else {
        reportError(result.message);
      }
    },
    [draft, token, refresh, reportError, reportSuccess],
  );

  const ordered = useMemo(() => {
    let list = sortBookings(bookings);
    if (statusFilter !== "ALL") {
      list = list.filter((b) => b.status === statusFilter);
    }
    if (flaggedOnly) {
      list = list.filter((b) => b.flagged === true);
    }
    const q = query.trim().toLowerCase();
    if (q.length > 0) {
      // Match the query as a case-insensitive substring of the client's name,
      // email, or requested date (so partial dates like "2026-07" also match).
      list = list.filter((b) =>
        [b.clientName, b.clientEmail, b.requestedDate]
          .filter((v): v is string => typeof v === "string")
          .some((v) => v.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [bookings, query, statusFilter, flaggedOnly]);

  /** True when any filter (text, status, flagged) is currently narrowing the list. */
  const filtersActive =
    query.trim().length > 0 || statusFilter !== "ALL" || flaggedOnly;

  // The booking currently being edited (drives the edit modal below).
  const editingBooking =
    editingId !== null
      ? bookings.find((b) => b.bookingId === editingId) ?? null
      : null;

  return (
    <AdminPanel
      title="Bookings"
      description="Every inquiry across all statuses, pending ones first. Accept or deny pending requests, or edit any booking's date, time, and status when plans change."
      status={status}
    >
      {!loading && bookings.length > 0 ? (
        <div className="admin-field admin-bookings__search">
          <label className="admin-field__label" htmlFor="bookings-search">
            Search
          </label>
          <input
            id="bookings-search"
            className="admin-field__input"
            type="search"
            placeholder="Search by name, email, or date"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="admin-bookings__filters">
            <label className="admin-field admin-bookings__status">
              <span className="admin-field__label">Status</span>
              <select
                className="admin-field__input"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as "ALL" | BookingStatus)
                }
              >
                <option value="ALL">All statuses</option>
                <option value={BookingStatus.PENDING}>Pending</option>
                <option value={BookingStatus.APPROVED}>Approved</option>
                <option value={BookingStatus.DENIED}>Denied</option>
                <option value={BookingStatus.EXPIRED}>Expired</option>
              </select>
            </label>
            <label className="admin-checkbox admin-bookings__flagged">
              <input
                type="checkbox"
                checked={flaggedOnly}
                onChange={(e) => setFlaggedOnly(e.target.checked)}
              />
              <span>Flagged only</span>
            </label>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="admin-empty">Loading bookings…</p>
      ) : bookings.length === 0 ? (
        <p className="admin-empty">No bookings yet.</p>
      ) : ordered.length === 0 ? (
        <p className="admin-empty">
          {filtersActive
            ? "No bookings match your filters."
            : "No bookings yet."}
        </p>
      ) : (
        <ul className="admin-list admin-list--cards">
          {ordered.map((booking) => {
            const conflicts = booking.conflicts;
            const hasOverlap = conflicts.overlappingCount > 0;
            const calendarConflicts = booking.calendarConflicts ?? [];
            const hasCalendarOverlap = calendarConflicts.length > 0;
            const hasConflict =
              hasOverlap || conflicts.calendarBusy || hasCalendarOverlap;
            const calendarDayUrl = googleCalendarDayUrl(booking.requestedDate);

            return (
              <li
                key={booking.bookingId}
                className="admin-list__row admin-list__row--stack"
              >
                <div className="admin-list__text">
                  <p className="admin-list__label">
                    {booking.clientName}{" "}
                    <span
                      className={`admin-badge admin-badge--${statusModifier(
                        booking.status,
                      )}`}
                    >
                      {booking.status}
                    </span>
                    {booking.flagged ? (
                      <span className="admin-badge admin-badge--flagged">
                        ⚠ Flagged
                      </span>
                    ) : null}
                    {booking.googleCalendarEventId ? (
                      <span className="admin-badge admin-badge--calendar">
                        On your calendar ✓
                      </span>
                    ) : booking.status === BookingStatus.APPROVED ? (
                      /* Approved but no calendar event: the write was skipped
                         (no calendar connected) or failed. Say so plainly
                         rather than leaving the owner to discover it in
                         Google Calendar. */
                      <span
                        className="admin-badge admin-badge--conflict"
                        title="This approved booking was not added to your Google Calendar. Check the Calendars tab — you may need to reconnect."
                      >
                        ⚠ Not on your calendar
                      </span>
                    ) : null}
                  </p>
                  <p className="admin-list__sub">{booking.clientEmail}</p>
                  <p className="admin-list__sub">{formatSchedule(booking)}</p>
                  {booking.message ? (
                    <p className="admin-list__message">{booking.message}</p>
                  ) : null}

                  {hasConflict ? (
                    <div className="admin-conflicts">
                      {hasOverlap ? (
                        <span className="admin-badge admin-badge--conflict">
                          Overlaps {conflicts.overlappingCount} approved booking
                          {conflicts.overlappingCount === 1 ? "" : "s"} that day
                        </span>
                      ) : null}
                      {hasCalendarOverlap ? (
                        <div className="admin-cal-conflicts">
                          <p className="admin-cal-conflicts__label">
                            Your calendar conflicts with this request:
                          </p>
                          <ul className="admin-cal-conflicts__list">
                            {calendarConflicts.map((event, index) => (
                              <li
                                key={`${event.when}-${event.title}-${index}`}
                                className="admin-cal-conflicts__item"
                              >
                                {event.when} — {event.title}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : conflicts.calendarBusy ? (
                        <span className="admin-badge admin-badge--conflict">
                          Calendar shows busy that day
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <p className="admin-conflicts admin-conflicts--clear">
                      No conflicts
                    </p>
                  )}
                  {calendarDayUrl ? (
                    <a
                      className="admin-cal-conflicts__link admin-card__cal-link"
                      href={calendarDayUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View this day in Google Calendar →
                    </a>
                  ) : null}
                </div>

                <div className="admin-list__actions">
                  {booking.status === BookingStatus.PENDING ? (
                    <>
                      <button
                        type="button"
                        className="admin-button admin-button--primary"
                        disabled={deciding !== null}
                        onClick={() => {
                          setCalendarTitle(defaultCalendarTitle(booking));
                          setPending({ kind: "accept", booking, notify: true });
                        }}
                      >
                        {deciding === booking.bookingId ? "Working…" : "Accept"}
                      </button>
                      <button
                        type="button"
                        className="admin-button admin-button--danger"
                        disabled={deciding !== null}
                        onClick={() =>
                          setPending({ kind: "deny", booking, notify: false })
                        }
                      >
                        {deciding === booking.bookingId ? "Working…" : "Deny"}
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="admin-button"
                    disabled={deciding !== null || editingId !== null}
                    onClick={() => {
                      setMessageDraft("");
                      setMessaging(booking);
                    }}
                    title="Email this client from the studio address (your personal email stays private)"
                  >
                    Message
                  </button>
                  <button
                    type="button"
                    className="admin-button"
                    disabled={deciding !== null || editingId !== null}
                    onClick={() => beginEdit(booking)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--danger"
                    disabled={deciding !== null || editingId !== null || removingId !== null}
                    onClick={() =>
                      setPending({ kind: "delete", booking, notify: false })
                    }
                  >
                    {removingId === booking.bookingId ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editingBooking && draft ? (
        <div
          className="admin-modal__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Edit booking"
        >
          <div className="admin-modal admin-modal--wide">
            <p className="admin-modal__title">
              {editingBooking.clientName}{" "}
              <span
                className={`admin-badge admin-badge--${statusModifier(
                  editingBooking.status,
                )}`}
              >
                {editingBooking.status}
              </span>
            </p>
            <label className="admin-field">
              <span className="admin-field__label">Date</span>
              <input
                className="admin-field__input"
                type="date"
                value={draft.requestedDate}
                onChange={(e) =>
                  setDraft({ ...draft, requestedDate: e.target.value })
                }
              />
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Start time</span>
              <input
                className="admin-field__input"
                type="time"
                value={draft.startTime}
                onChange={(e) =>
                  setDraft({ ...draft, startTime: e.target.value })
                }
              />
            </label>
            <label className="admin-field">
              <span className="admin-field__label">Duration (minutes)</span>
              <input
                className="admin-field__input"
                type="number"
                min={15}
                max={720}
                value={draft.durationMinutes}
                onChange={(e) =>
                  setDraft({ ...draft, durationMinutes: e.target.value })
                }
              />
            </label>
            {editingBooking.status === BookingStatus.DENIED ? (
              <label className="admin-field">
                <span className="admin-field__label">
                  Reconsider this rejected booking
                </span>
                <select
                  className="admin-field__input"
                  value={draft.status}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      status: e.target.value as BookingStatus,
                    })
                  }
                >
                  <option value={BookingStatus.DENIED}>Keep denied</option>
                  <option value={BookingStatus.PENDING}>
                    Reopen as pending
                  </option>
                  <option value={BookingStatus.APPROVED}>
                    Approve (emails the client)
                  </option>
                </select>
              </label>
            ) : null}
            {editingBooking.status === BookingStatus.APPROVED ? (
              <label className="admin-field">
                <span className="admin-field__label">
                  Change this approved booking
                </span>
                <select
                  className="admin-field__input"
                  value={draft.status}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      status: e.target.value as BookingStatus,
                    })
                  }
                >
                  <option value={BookingStatus.APPROVED}>Keep approved</option>
                  <option value={BookingStatus.DENIED}>
                    Cancel (denies &amp; emails the client)
                  </option>
                  <option value={BookingStatus.PENDING}>
                    Reopen as pending
                  </option>
                </select>
              </label>
            ) : null}
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={editNotify}
                onChange={(e) => setEditNotify(e.target.checked)}
              />
              <span>Email the requester about this change</span>
            </label>
            <div className="admin-list__actions">
              <button
                type="button"
                className="admin-button admin-button--primary"
                disabled={saving}
                onClick={() => void saveEdit(editingBooking.bookingId, editNotify)}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                className="admin-button"
                disabled={saving}
                onClick={cancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {messaging ? (
        <div
          className="admin-modal__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Message client"
        >
          <div className="admin-modal admin-modal--wide">
            <p className="admin-modal__title">
              Message {messaging.clientName}
            </p>
            <p className="admin-modal__body">
              Sent from the studio address — your personal email address is not
              shown to the client, and their reply comes back to you.
            </p>
            <label className="admin-field">
              <span className="admin-field__label">Your message</span>
              <textarea
                className="admin-field__input admin-field__textarea"
                rows={6}
                maxLength={2000}
                autoFocus
                placeholder="Hi! Thanks for your inquiry — I wanted to ask about…"
                value={messageDraft}
                onChange={(event) => setMessageDraft(event.target.value)}
              />
            </label>
            <div className="admin-list__actions">
              <button
                type="button"
                className="admin-button admin-button--primary"
                disabled={sendingMessage || messageDraft.trim().length === 0}
                onClick={() => void sendMessage()}
              >
                {sendingMessage ? "Sending…" : "Send message"}
              </button>
              <button
                type="button"
                className="admin-button"
                disabled={sendingMessage}
                onClick={() => {
                  setMessaging(null);
                  setMessageDraft("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pending ? (
        <div
          className="admin-modal__overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm action"
        >
          <div className="admin-modal">
            <p className="admin-modal__title">
              {pending.kind === "delete"
                ? "Delete this booking?"
                : pending.kind === "accept"
                  ? "Approve this booking?"
                  : "Deny this booking?"}
            </p>
            <p className="admin-modal__body">
              {pending.booking.clientName} · {pending.booking.requestedDate}
              {pending.kind === "delete"
                ? " — this permanently removes the request and cannot be undone."
                : ""}
            </p>
            {pending.kind === "accept" ? (
              <label className="admin-field">
                <span className="admin-field__label">
                  Calendar event title
                </span>
                <input
                  className="admin-field__input"
                  type="text"
                  maxLength={1024}
                  value={calendarTitle}
                  onChange={(e) => setCalendarTitle(e.target.value)}
                />
                <span className="admin-field__hint">
                  Added to your Google Calendar when connected. Edit as you like.
                </span>
              </label>
            ) : null}
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={pending.notify}
                onChange={(e) =>
                  setPending({ ...pending, notify: e.target.checked })
                }
              />
              <span>Email the requester about this</span>
            </label>
            <div className="admin-list__actions">
              <button
                type="button"
                className={`admin-button ${
                  pending.kind === "accept"
                    ? "admin-button--primary"
                    : "admin-button--danger"
                }`}
                onClick={() => void confirmPending()}
              >
                {pending.kind === "delete"
                  ? "Delete"
                  : pending.kind === "accept"
                    ? "Approve"
                    : "Deny"}
              </button>
              <button
                type="button"
                className="admin-button"
                onClick={() => setPending(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminPanel>
  );
}
